import { toSafeString } from './toSafeString.js';

const PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG = Object.freeze({
  analyzing: { running: 'Analyzing', completed: 'Analyzed' },
  checking: { running: 'Checking', completed: 'Checked' },
  reviewing: { running: 'Reviewing', completed: 'Reviewed' },
  looking_for: { running: 'Looking for', completed: 'Looked for' },
  searching_for: { running: 'Searching for', completed: 'Searched for' },
  finding: { running: 'Finding', completed: 'Found' },
  comparing: { running: 'Comparing', completed: 'Compared' },
  collecting: { running: 'Collecting', completed: 'Collected' },
  gathering: { running: 'Gathering', completed: 'Gathered' },
  summarizing: { running: 'Summarizing', completed: 'Summarized' },
  preparing: { running: 'Preparing', completed: 'Prepared' },
  organizing: { running: 'Organizing', completed: 'Organized' },
  sorting: { running: 'Sorting', completed: 'Sorted' },
  matching: { running: 'Matching', completed: 'Matched' },
  verifying: { running: 'Verifying', completed: 'Verified' },
  validating: { running: 'Validating', completed: 'Validated' },
  researching: { running: 'Researching', completed: 'Researched' },
  investigating: { running: 'Investigating', completed: 'Investigated' },
  calculating: { running: 'Calculating', completed: 'Calculated' },
  drafting: { running: 'Drafting', completed: 'Drafted' },
  creating: { running: 'Creating', completed: 'Created' },
  updating: { running: 'Updating', completed: 'Updated' },
  choosing: { running: 'Choosing', completed: 'Chose' },
  planning: { running: 'Planning', completed: 'Planned' },
  cleaning: { running: 'Cleaning', completed: 'Cleaned' },
  extracting: { running: 'Extracting', completed: 'Extracted' }
});

const PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTIONS = Object.freeze(Object.keys(PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG));
const DISPLAY_LABEL_PRESENT_TO_PAST_CONFIG = Object.freeze({
  ...Object.fromEntries(
    Object.entries(PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG).map(([action, config]) => {
      return [config.running.toLowerCase(), config.completed];
    })
  ),
  looking: 'Looked',
  searching: 'Searched',
  listing: 'Listed',
  counting: 'Counted',
  reading: 'Read',
  writing: 'Wrote',
  moving: 'Moved',
  deleting: 'Deleted',
  exploring: 'Explored',
  confirming: 'Confirmed',
  requesting: 'Requested',
  running: 'Ran',
  working: 'Worked',
  waiting: 'Waited',
  arranging: 'Arranged',
  adding: 'Added',
  starting: 'Started',
  refreshing: 'Refreshed',
  testing: 'Tested',
  saving: 'Saved',
  taking: 'Took',
  asking: 'Asked',
  // Present to past only: each past form also opens plain phrases ("shared drive files", "sent invoices").
  setting: 'Set',
  sending: 'Sent',
  sharing: 'Shared',
  refining: 'Refined'
});
const DISPLAY_LABEL_PAST_TO_PRESENT_CONFIG = Object.freeze({
  ...Object.fromEntries(
    Object.entries(PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG).map(([, config]) => {
      return [config.completed.toLowerCase(), config.running];
    })
  ),
  looked: 'Looking',
  searched: 'Searching',
  listed: 'Listing',
  counted: 'Counting',
  read: 'Reading',
  wrote: 'Writing',
  moved: 'Moving',
  deleted: 'Deleting',
  explored: 'Exploring',
  confirmed: 'Confirming',
  ran: 'Running',
  worked: 'Working',
  waited: 'Waiting',
  arranged: 'Arranging',
  added: 'Adding',
  started: 'Starting',
  refreshed: 'Refreshing',
  tested: 'Testing',
  saved: 'Saving',
  took: 'Taking',
  asked: 'Asking'
});
const DISPLAY_LABEL_PRESENT_PREFIXES = Object.freeze(Object.keys(DISPLAY_LABEL_PRESENT_TO_PAST_CONFIG).sort((left, right) => right.length - left.length));
const DISPLAY_LABEL_PAST_PREFIXES = Object.freeze(Object.keys(DISPLAY_LABEL_PAST_TO_PRESENT_CONFIG).sort((left, right) => right.length - left.length));
const GERUND_FOLLOWING_WORDS = Object.freeze(new Set(['a', 'an', 'the', 'this', 'that', 'these', 'those', 'my', 'your', 'our', 'their', 'his', 'her', 'its', 'some', 'any', 'each', 'every', 'another', 'to', 'for', 'from', 'with', 'without', 'through', 'around', 'into', 'onto', 'over', 'under', 'before', 'after', 'while', 'where', 'when', 'why', 'how']));

const toSafeDisplayTextPart = (value) => {
  return toSafeString(value).replace(/\s+/g, ' ').trim();
};

const capitalizeFirstCharacter = (value) => {
  const normalized = toSafeString(value);

  if (!normalized) {
    return '';
  }

  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const findMatchingDisplayLabelPrefix = ({ label, prefixes } = {}) => {
  const normalized = toSafeDisplayTextPart(label).toLowerCase();

  if (!normalized) {
    return null;
  }

  return (
    prefixes.find((prefix) => {
      return normalized === prefix || normalized.startsWith(`${prefix} `);
    }) || null
  );
};

const replaceLeadingDisplayLabelPhrase = ({ label, replacementsConfig, prefixes } = {}) => {
  const normalized = toSafeDisplayTextPart(label);

  if (!normalized) {
    return '';
  }

  const matchingPrefix = findMatchingDisplayLabelPrefix({
    label: normalized,
    prefixes
  });

  if (!matchingPrefix) {
    return normalized;
  }

  const replacementPhrase = replacementsConfig[matchingPrefix];

  if (!replacementPhrase) {
    return normalized;
  }

  const restText = normalized.slice(matchingPrefix.length).trimStart();
  const formattedReplacement = normalized[0] === normalized[0]?.toUpperCase() ? capitalizeFirstCharacter(replacementPhrase) : replacementPhrase;

  return restText ? `${formattedReplacement} ${restText}` : formattedReplacement;
};

const startsWithDisplayActionPhrase = (label) => {
  return Boolean(
    findMatchingDisplayLabelPrefix({
      label,
      prefixes: DISPLAY_LABEL_PRESENT_PREFIXES
    }) ||
    findMatchingDisplayLabelPrefix({
      label,
      prefixes: DISPLAY_LABEL_PAST_PREFIXES
    })
  );
};

const startsWithGerundVerbPhrase = (label) => {
  const words = toSafeDisplayTextPart(label).toLowerCase().split(' ').filter(Boolean);
  const [firstWord = '', secondWord = ''] = words;

  if (!/^[a-z]+ing$/.test(firstWord)) {
    return false;
  }

  return GERUND_FOLLOWING_WORDS.has(secondWord) || words.length >= 3;
};

const toNormalizedDisplayIntent = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const action = toSafeString(value?.action || value?.verb).toLowerCase();
  const subject = toSafeDisplayTextPart(value?.subject || value?.object);

  if (!PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG[action] || !subject) {
    return null;
  }

  return {
    action,
    subject
  };
};

const resolvePieveToolCallDisplayIntentText = ({ displayIntent, completed = false } = {}) => {
  const normalizedDisplayIntent = toNormalizedDisplayIntent(displayIntent);

  if (!normalizedDisplayIntent) {
    return null;
  }

  const actionConfig = PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG[normalizedDisplayIntent.action];
  const subject = normalizedDisplayIntent.subject;

  if (startsWithGerundVerbPhrase(subject) || startsWithDisplayActionPhrase(subject)) {
    const resolvedSubject = completed ? toPastTenseDisplayLabel(subject) : toPresentTenseDisplayLabel(subject);

    return capitalizeFirstCharacter(resolvedSubject);
  }

  const prefix = completed ? actionConfig.completed : actionConfig.running;

  return `${prefix} ${subject}`.trim();
};

const toPastTenseDisplayLabel = (label) => {
  return replaceLeadingDisplayLabelPhrase({
    label,
    replacementsConfig: DISPLAY_LABEL_PRESENT_TO_PAST_CONFIG,
    prefixes: DISPLAY_LABEL_PRESENT_PREFIXES
  });
};

const toPresentTenseDisplayLabel = (label) => {
  return replaceLeadingDisplayLabelPhrase({
    label,
    replacementsConfig: DISPLAY_LABEL_PAST_TO_PRESENT_CONFIG,
    prefixes: DISPLAY_LABEL_PAST_PREFIXES
  });
};

export { PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTION_CONFIG, PIEVE_TOOL_CALL_DISPLAY_INTENT_ACTIONS, toPastTenseDisplayLabel, toPresentTenseDisplayLabel, toNormalizedDisplayIntent, resolvePieveToolCallDisplayIntentText };
